import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles, Zap, Shield } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

const HomePage = () => {
  const { t } = useTranslation();
  const { scrollYProgress } = useScroll();
  const heroRef = useRef(null);

  // Parallax effects
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  // Real MTG card images from user uploads - TODAS las cartas (7 total)
  const allMTGCards = [
    {
      url: 'https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/i1k29pgp_Forest_%20%283%29.png',
      name: 'Forest',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/ccpmww35_Frieren.png',
      name: 'Frieren',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/snxxvkue_sld-1596-harmonize.jpg',
      name: 'Harmonize',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/9yyni6qw_sta-95-sign-in-blood.jpg',
      name: 'Sign in Blood',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/3uh58isa_Exquisite-Blood.png',
      name: 'Exquisite Blood',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/9cle0d7p_been-working-on-some-elden-ring-mtg-proxies-figured-id-v0-8jebto8tni9e1.jpg',
      name: 'Maliketh Black Blade',
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/2kqfhzam_tmp_mf4ht7mxqjyx0lcps4e.png',
      name: 'Ghost Knight of Hallownest',
    }
  ];

  // Seleccionar 4 cartas aleatorias para mostrar (cambiarán en cada carga)
  const [displayedCards] = useState(() => {
    const shuffled = [...allMTGCards].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4).map((card, index) => ({
      ...card,
      rotation: [-15, 8, -8, 12][index],
      position: [
        { left: '10%', top: '20%' },
        { right: '15%', top: '15%' },
        { left: '8%', bottom: '25%' },
        { right: '12%', bottom: '20%' }
      ][index]
    }));
  });

  const tiers = [
    {
      name: t('home.tiers.base.name') || 'Base Cards',
      color: 'tier-plus',
      price: '0.40€',
      description: t('home.tiers.base.desc') || 'Laminated cards with improved color consistency and durability',
      features: ['Laminated finish', 'Better visual fidelity', 'Enhanced durability', 'Quality control'],
      category: 'base',
      popular: true,
      icon: Zap,
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      name: t('home.tiers.elite.name') || 'Elite Cards',
      color: 'tier-elite',
      price: '0.50€',
      description: t('home.tiers.elite.desc') || 'Premium finish with high attention to detail',
      features: ['Premium laminated', 'Strong visual realism', 'Near-authentic feel', 'Top quality'],
      category: 'elite',
      icon: Sparkles,
      gradient: 'from-rose-500 to-pink-600'
    },
    {
      name: t('home.tiers.foil.name') || 'Foil Cards',
      color: 'tier-foil',
      price: '0.90€',
      description: t('home.tiers.foil.desc') || 'Holographic foil for collectors',
      features: ['Holographic foil front', 'Laminated & reinforced', 'Black core', 'Collectible feel'],
      category: 'foil',
      icon: Sparkles,
      gradient: 'from-amber-400 via-yellow-500 to-orange-600'
    },
    {
      name: t('home.tiers.custom.name') || 'Custom Cards',
      color: 'tier-custom',
      price: '0.60€',
      description: t('home.tiers.custom.desc') || 'Fully personalized with your own artwork',
      features: ['Upload your design', 'Choice of frame type', 'High-quality printing', 'Unique creations'],
      category: 'custom',
      icon: Shield,
      gradient: 'from-violet-500 to-purple-700'
    }
  ];

  return (
    <>
      <Navigation />
      
      {/* Hero Section with VIBRANT MTG Art Background */}
      <motion.section 
        ref={heroRef}
        style={{ y: heroY, opacity: heroOpacity }}
        className="relative min-h-screen flex items-center justify-center px-6 pt-20 overflow-hidden"
      >
        {/* Dawnhand Eulogist MTG Art Background */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/nkw0ij61_evyn-fong-dawnhandeulogist-signed-small.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(8px) brightness(0.4)',
            transform: 'scale(1.1)'
          }}
        ></div>

        {/* Colorful Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/60 via-purple-900/50 to-pink-900/60 z-0"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-0"></div>

        {/* Animated Light Particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full opacity-60"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}

        {/* REAL MTG Cards Floating - SIN MOVIMIENTO DE RATON */}
        {displayedCards.map((card, index) => (
          <motion.div
            key={index}
            className="absolute w-56 h-80 rounded-2xl shadow-2xl overflow-hidden border-4 border-white/20 pointer-events-none z-10"
            style={{
              ...card.position,
              rotate: card.rotation,
              backgroundImage: `url(${card.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '0 25px 50px -12px rgba(139, 92, 246, 0.5)'
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [card.rotation, card.rotation + 5, card.rotation],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 4 + index,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.5
            }}
            whileHover={{ scale: 1.1, rotate: 0, zIndex: 50 }}
          >
            {/* Glow effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-violet-600/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
          </motion.div>
        ))}
        
        <div className="relative z-20 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Glowing Badge */}
            <motion.div
              className="inline-block mb-6 px-6 py-2 bg-gradient-to-r from-violet-600 to-pink-600 rounded-full text-white text-sm font-semibold"
              animate={{ boxShadow: ['0 0 20px rgba(139, 92, 246, 0.5)', '0 0 40px rgba(236, 72, 153, 0.5)', '0 0 20px rgba(139, 92, 246, 0.5)'] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              ✨ Premium MTG Proxies
            </motion.div>

            <motion.h1 
              className="text-5xl md:text-6xl lg:text-7xl font-outfit font-bold tracking-tight text-white mb-6"
              style={{
                textShadow: '0 0 40px rgba(139, 92, 246, 0.8), 0 0 80px rgba(236, 72, 153, 0.5)'
              }}
            >
              {t('home.heroTitle1')}<br />
              <span className="bg-gradient-to-r from-violet-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                {t('home.heroTitle2')}
              </span>
            </motion.h1>
          </motion.div>

          <motion.p 
            className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto font-medium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)' }}
          >
            {t('home.heroSubtitle')}
          </motion.p>

          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link
              to="/shop"
              className="group px-10 py-5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-violet-500 hover:to-purple-500 transition-all shadow-2xl hover:shadow-violet-500/50 flex items-center space-x-3 hover:scale-105"
            >
              <Sparkles size={24} className="group-hover:rotate-180 transition-transform duration-500" />
              <span>{t('home.browseProducts')}</span>
              <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
            </Link>
            <Link
              to="/shop?category=commander"
              className="px-10 py-5 bg-white/10 backdrop-blur-md text-white border-2 border-white/30 rounded-xl font-bold text-lg hover:bg-white/20 transition-all shadow-2xl hover:scale-105"
            >
              {t('home.commanderDecks')}
            </Link>
          </motion.div>
        </div>

        {/* Enhanced Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20"
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center backdrop-blur-sm bg-white/10">
            <motion.div
              className="w-2 h-4 bg-gradient-to-b from-violet-400 to-pink-400 rounded-full mt-2"
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </motion.section>

      {/* Vibrant Stats Section */}
      <section className="py-20 px-6 relative overflow-hidden">
        {/* Colorful background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900 via-purple-900 to-pink-900"></div>
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '28', label: 'Products', color: 'from-blue-400 to-cyan-400' },
              { number: '4', label: 'Quality Tiers', color: 'from-violet-400 to-purple-400' },
              { number: '100%', label: 'Custom', color: 'from-pink-400 to-rose-400' },
              { number: '€0.40', label: 'Starting Price', color: 'from-amber-400 to-orange-400' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className={`text-5xl md:text-6xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}>
                  {stat.number}
                </div>
                <div className="text-white/80 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tier Comparison - More Colorful */}
      <section className="py-24 px-6 relative overflow-hidden bg-black">
        {/* Animated gradient background */}
        <motion.div 
          className="absolute inset-0 opacity-20"
          animate={{
            background: [
              'radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.3), transparent 50%)',
              'radial-gradient(circle at 80% 50%, rgba(236, 72, 153, 0.3), transparent 50%)',
              'radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.3), transparent 50%)',
            ]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-outfit font-bold tracking-tight text-white mb-4">
              {t('home.tiersTitle')}
            </h2>
            <p className="text-white/70 text-xl">{t('home.tiersSubtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier, index) => {
              const Icon = tier.icon;
              return (
                <motion.div
                  key={index}
                  className={`relative bg-black/50 backdrop-blur-sm border-2 ${tier.popular ? 'border-violet-500' : 'border-white/10'} rounded-2xl p-6 hover:-translate-y-3 transition-all duration-300 group overflow-hidden`}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.03, boxShadow: '0 25px 50px -12px rgba(139, 92, 246, 0.5)' }}
                >
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${tier.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
                  
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-600 to-pink-600 text-white text-xs font-bold px-4 py-1 rounded-full z-10 shadow-lg">
                      {t('home.mostPopular')}
                    </div>
                  )}
                  
                  <div className="relative z-10">
                    <div className={`mb-4 w-16 h-16 rounded-xl bg-gradient-to-br ${tier.gradient} flex items-center justify-center shadow-lg`}>
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-outfit font-bold text-white mb-2">{tier.name}</h3>
                    <div className={`text-4xl font-black bg-gradient-to-r ${tier.gradient} bg-clip-text text-transparent mb-4`}>
                      from {tier.price}
                    </div>
                    <p className="text-white/70 text-sm mb-6">{tier.description}</p>
                    
                    <ul className="space-y-2 mb-8">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-white/60">
                          <Check size={16} className="text-violet-400 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={`/shop?category=${tier.category}`}
                      className={`block w-full text-center px-6 py-3 bg-gradient-to-r ${tier.gradient} text-white rounded-xl font-bold hover:shadow-lg transition-all`}
                    >
                      {t('home.viewProducts')}
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works - With Color */}
      <section className="py-24 px-6 relative overflow-hidden bg-gradient-to-br from-purple-900 via-violet-900 to-indigo-900">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-outfit font-bold tracking-tight text-white mb-4">
              {t('home.howItWorksTitle')}
            </h2>
            <p className="text-white/80 text-xl">{t('home.howItWorksSubtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { 
                num: 1, 
                title: t('home.step1') || 'Choose Your Cards', 
                desc: t('home.step1desc') || 'Select from our quality tiers and add cards to your order', 
                image: 'https://images.unsplash.com/photo-1732944710507-feecdfb4f2c1?w=400' 
              },
              { 
                num: 2, 
                title: t('home.step2') || 'Paste Your Decklist', 
                desc: t('home.step2desc') || 'Simply paste your card names or build card-by-card', 
                image: 'https://images.unsplash.com/photo-1593814681464-eef5af2b0628?w=400' 
              },
              { 
                num: 3, 
                title: t('home.step3') || 'We Print & Ship', 
                desc: t('home.step3desc') || 'High-quality printing with attention to detail, shipped to your door', 
                image: 'https://images.unsplash.com/photo-1513002433973-e0a181372d60?w=400' 
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="text-center group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <motion.div
                  className="relative w-40 h-56 mx-auto mb-6 rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <img src={step.image} alt={step.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-violet-900/70 to-transparent flex items-end justify-center pb-4">
                    <span className="text-6xl font-black text-white">{step.num}</span>
                  </div>
                </motion.div>
                <h3 className="text-2xl font-outfit font-bold text-white mb-2">{step.title}</h3>
                <p className="text-white/70">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with MTG Art */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_371c3e43-0c79-40b3-81cb-e054f2680b6f/artifacts/ccpmww35_Frieren.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(10px) brightness(0.3)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-purple-900/80 to-black"></div>

        <motion.div 
          className="max-w-4xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-outfit font-bold tracking-tight text-white mb-6">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-white/90 text-xl mb-10">
            {t('home.ctaSubtitle')}
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center space-x-3 px-12 py-6 bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 text-white rounded-2xl font-bold text-xl hover:scale-105 transition-all shadow-2xl hover:shadow-violet-500/50 group"
          >
            <Sparkles size={28} className="group-hover:rotate-180 transition-transform duration-500" />
            <span>{t('home.ctaButton')}</span>
            <ArrowRight size={28} className="group-hover:translate-x-2 transition-transform" />
          </Link>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default HomePage;
